import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { driver, type Driver, type DriverHook, type DriveStep, type PopoverDOM } from 'driver.js';
import 'driver.js/dist/driver.css';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../../auth/AuthProvider';
import {
  analyticsStep,
  getProductTourEntryPoint,
  recordProductTourEventBestEffort,
} from '../analytics/product-tour-analytics';

import {
  CANONICAL_PRODUCT_TOUR_CONTRACT_ID,
  getProductTourState,
  transitionProductTourState,
  type ProductTourState,
} from '../../product-guidance/api/product-tour-state.api';
import type { ProductTourAnalyticsStepId } from '../../product-guidance/types/product-guidance';

export interface TourGuideMoment {
  id: ProductTourAnalyticsStepId;
  order: number;
  route: string;
  selector: string;
  mobileSelector?: string;
  title: string;
  message: string;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
}

export const TOUR_GUIDE_MOMENTS: readonly TourGuideMoment[] = [
  { id: 'tour-step-01', order: 1, route: '/tour/machine', selector: '[data-tour-target="machine-overview"]', title: 'Meet one machine', message: 'Everything Route42 learns is organized around the physical machine your team is trying to understand.', side: 'bottom', align: 'start' },
  { id: 'tour-step-02', order: 2, route: '/tour/documents', selector: '[data-tour-target="evidence-list"]', title: 'Start with evidence', message: 'Route42 starts with evidence your team already has and turns it into usable machine context.', side: 'right', align: 'start' },
  { id: 'tour-step-03', order: 3, route: '/tour/documents', selector: '[data-tour-target="evidence-workspace"]', title: 'Different sources tell different parts', message: 'A manual, maintenance history, and an equipment image each contribute a different part of the machine story.', side: 'top', align: 'center' },
  { id: 'tour-step-04', order: 4, route: '/tour/conversation', selector: '[data-tour-target="machine-question"]', title: 'Ask the machine', message: 'Conversations let the team ask practical machine questions using the evidence Route42 has connected.', side: 'left', align: 'start' },
  { id: 'tour-step-05', order: 5, route: '/tour/conversation', selector: '[data-tour-target="grounded-answer"]', title: 'See why the answer is supported', message: 'The value is not just the answer. Route42 shows the evidence supporting it and where the evidence is still limited.', side: 'left', align: 'start' },
  { id: 'tour-step-06', order: 6, route: '/tour/investigation', selector: '[data-tour-target="investigation-assessment"]', title: 'Move persistent problems into an Investigation', message: 'When a problem needs continued reasoning, Route42 keeps the work together in an Investigation instead of losing it in chat.', side: 'bottom', align: 'start' },
  { id: 'tour-step-07', order: 7, route: '/tour/investigation', selector: '[data-tour-target="investigation-reasoning"]', mobileSelector: '[data-tour-target="investigation-reasoning-mobile"]', title: 'See how shared understanding changes', message: 'People contribute observations and measurements. Route42 connects them to evidence, updates where things stand, and keeps what we know, possible causes, and next checks organized.', side: 'right', align: 'start' },
  { id: 'tour-step-08', order: 8, route: '/tour/memory', selector: '[data-tour-target="machine-memory"]', title: 'Let Machine Memory grow', message: 'Machine Memory preserves supported understanding so the next question starts with what Route42 has already learned.', side: 'bottom', align: 'start' },
  { id: 'tour-step-09', order: 9, route: '/tour/activity', selector: '[data-tour-target="activity-timeline"]', title: 'See how understanding developed', message: "Activity shows how the machine workspace changed and how Route42's understanding developed over time.", side: 'top', align: 'start' },
  { id: 'tour-step-10', order: 10, route: '/tour/summary', selector: '[data-tour-target="tour-summary"]', title: 'Know where to get help', message: 'Add evidence. Ask questions. Investigate what matters. Let Machine Memory grow—and ask Ace whenever you need help using Route42.', side: 'bottom', align: 'center' },
] as const;

interface TourGuideContextValue {
  state: ProductTourState | null;
  stateLoading: boolean;
  stateError: string | null;
  active: boolean;
  activeStepId: string | null;
  startOrResume: () => Promise<void>;
  skip: () => Promise<void>;
  pause: () => void;
  refreshState: () => Promise<void>;
}

const TourGuideContext = createContext<TourGuideContextValue | null>(null);

type DriverHookElement = Parameters<DriverHook>[0];
type DriverHookStep = Parameters<DriverHook>[1];
type DriverHookOptions = Parameters<DriverHook>[2];

const TOUR_POPOVER_TITLE_ID = 'r42-tour-driver-title';
const TOUR_POPOVER_DESCRIPTION_ID = 'r42-tour-driver-description';

function applyTourPopoverAccessibility(popover?: PopoverDOM) {
  const livePopover = (popover?.wrapper.matches('.driver-popover') ? popover.wrapper : null)
    ?? document.querySelector<HTMLElement>('.driver-popover.r42-tour-driver-popover');
  if (!livePopover) return;

  const title = popover?.title ?? livePopover.querySelector<HTMLElement>('.driver-popover-title');
  const description = popover?.description ?? livePopover.querySelector<HTMLElement>('.driver-popover-description');
  if (!title || !description) return;

  title.id = TOUR_POPOVER_TITLE_ID;
  description.id = TOUR_POPOVER_DESCRIPTION_ID;
  livePopover.setAttribute('role', 'dialog');
  livePopover.setAttribute('aria-modal', 'true');
  livePopover.setAttribute('aria-labelledby', TOUR_POPOVER_TITLE_ID);
  livePopover.setAttribute('aria-describedby', TOUR_POPOVER_DESCRIPTION_ID);
  livePopover.removeAttribute('aria-hidden');
  livePopover.removeAttribute('aria-label');
}

export function TourGuideProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const { activeCustomerId } = useAuth();
  const [state, setState] = useState<ProductTourState | null>(null);
  const [stateLoading, setStateLoading] = useState(true);
  const [stateError, setStateError] = useState<string | null>(null);
  const [active, setActive] = useState(false);
  const [activeStepId, setActiveStepId] = useState<string | null>(null);
  const driverRef = useRef<Driver | null>(null);
  const transitionBusyRef = useRef(false);
  const destroyModeRef = useRef<'PAUSE' | 'SKIP' | 'COMPLETE' | null>(null);

  const refreshState = useCallback(async () => {
    setStateLoading(true);
    if (!activeCustomerId) {
      setState(null);
      setStateError(null);
      setStateLoading(false);
      return;
    }
    try {
      const next = await getProductTourState();
      setState(next);
      setStateError(null);
    } catch {
      setStateError('Tour progress could not be loaded. You can still use the guided example, but progress may not be saved.');
    } finally {
      setStateLoading(false);
    }
  }, [activeCustomerId]);

  // Refresh persisted tour state whenever organization context changes. This
  // effect must not own Driver.js teardown: activeCustomerId can settle or
  // change independently of the walkthrough, and an effect cleanup here would
  // otherwise destroy a tour that has just started.
  useEffect(() => {
    void refreshState();
  }, [refreshState]);

  // Driver.js belongs to the Product Tour provider lifecycle, not the
  // organization-scoped state-refresh lifecycle. Only tear it down when this
  // provider actually unmounts.
  useEffect(() => () => {
    destroyModeRef.current = 'PAUSE';
    driverRef.current?.destroy();
    driverRef.current = null;
  }, []);

  const persist = useCallback(async (action: 'START' | 'STEP_COMPLETED' | 'SKIP' | 'COMPLETE' | 'REPLAY', stepId?: string) => {
    if (!activeCustomerId) return null;
    try {
      const next = await transitionProductTourState(action, stepId, CANONICAL_PRODUCT_TOUR_CONTRACT_ID);
      setState(next);
      setStateError(null);
      return next;
    } catch {
      setStateError('Tour progress could not be saved. The walkthrough can continue, and your machine data is unaffected.');
      return null;
    }
  }, [activeCustomerId]);

  const waitForTourTarget = useCallback(async (selector: string, timeoutMs = 6000): Promise<boolean> => {
    const deadline = window.performance.now() + timeoutMs;
    while (window.performance.now() < deadline) {
      if (document.querySelector(selector)) return true;
      await new Promise<void>((resolve) => window.setTimeout(resolve, 40));
    }
    return Boolean(document.querySelector(selector));
  }, []);

  const routeToIndex = useCallback(async (index: number, driverObj: Driver, mode: 'drive' | 'move') => {
    const moment = TOUR_GUIDE_MOMENTS[index];
    if (!moment) return false;
    const selector = selectorForViewport(moment);

    // React owns routing; Driver.js owns highlighting. Never ask Driver.js to
    // advance while the destination route is still rendering. This is the
    // deterministic cross-route handoff: navigate, wait for the exact target to
    // exist in the committed DOM, then activate that indexed Driver.js step.
    navigate(moment.route);
    const targetReady = await waitForTourTarget(selector);

    // The walkthrough may have been paused/skipped while navigation was pending.
    if (driverRef.current !== driverObj) return false;

    if (!targetReady) {
      setStateError('The guided tour could not open the next example area. You can continue exploring the Product Tour normally.');
      destroyModeRef.current = 'PAUSE';
      driverObj.destroy();
      driverRef.current = null;
      setActive(false);
      setActiveStepId(null);
      return false;
    }

    try {
      if (mode === 'drive') driverObj.drive(index);
      else driverObj.moveTo(index);
      return true;
    } catch (reason) {
      const detail = reason instanceof Error ? reason.message : 'Unknown Driver.js error.';
      setStateError(`The guided tour could not highlight this example area: ${detail}`);
      destroyModeRef.current = 'PAUSE';
      driverObj.destroy();
      driverRef.current = null;
      setActive(false);
      setActiveStepId(null);
      return false;
    }
  }, [navigate, waitForTourTarget]);

  const createDriver = useCallback(() => {
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    const steps: DriveStep[] = TOUR_GUIDE_MOMENTS.map((moment) => ({
      element: selectorForViewport(moment),
      waitForElement: 5000,
      skipMissingElement: false,
      disableActiveInteraction: false,
      popover: {
        title: moment.title,
        description: moment.message,
        side: moment.side,
        align: moment.align,
      },
    }));

    const driverObj = driver({
      steps,
      animate: !reducedMotion,
      duration: reducedMotion ? 0 : 260,
      smoothScroll: !reducedMotion,
      allowScroll: true,
      overlayOpacity: 0.48,
      stagePadding: 9,
      stageRadius: 12,
      popoverOffset: 12,
      popoverClass: 'r42-tour-driver-popover',
      showButtons: ['previous', 'next', 'close'],
      showProgress: true,
      progressText: 'Step {{current}} of {{total}}',
      prevBtnText: 'Back',
      nextBtnText: 'Continue',
      doneBtnText: 'Finish tour',
      allowKeyboardControl: true,
      overlayClickBehavior: 'close',
      onPopoverRender: (popover: PopoverDOM) => {
        popover.closeButton.textContent = 'Skip';
        popover.closeButton.setAttribute('aria-label', 'Skip guided walkthrough');
        popover.closeButton.setAttribute('title', 'Skip guided walkthrough');
        applyTourPopoverAccessibility(popover);
      },
      onHighlighted: (_element: DriverHookElement, _step: DriverHookStep, options: DriverHookOptions) => {
        const index = options.index ?? options.driver.getActiveIndex() ?? 0;
        const moment = TOUR_GUIDE_MOMENTS[index];
        setActive(true);
        setActiveStepId(moment?.id ?? null);
        // Driver.js may finish mutating/repositioning the popover after the render hook.
        // Re-apply dialog semantics to the live popover after highlighting so the
        // accessibility contract survives the library's internal render cycle.
        window.requestAnimationFrame(() => applyTourPopoverAccessibility());
        if (moment) recordProductTourEventBestEffort({ event_type: 'TOUR_STEP_VIEWED', step_id: moment.id, step_order: moment.order });
      },
      onNextClick: async (_element: DriverHookElement, _step: DriverHookStep, options: DriverHookOptions) => {
        if (transitionBusyRef.current) return;
        transitionBusyRef.current = true;
        const index = options.index ?? options.driver.getActiveIndex() ?? 0;
        const moment = TOUR_GUIDE_MOMENTS[index];
        if (moment) {
          await persist('STEP_COMPLETED', moment.id);
          recordProductTourEventBestEffort({ event_type: 'TOUR_STEP_COMPLETED', step_id: moment.id, step_order: moment.order });
        }
        const nextIndex = index + 1;
        if (nextIndex < TOUR_GUIDE_MOMENTS.length) {
          await routeToIndex(nextIndex, options.driver, 'move');
        }
        transitionBusyRef.current = false;
      },
      onPrevClick: async (_element: DriverHookElement, _step: DriverHookStep, options: DriverHookOptions) => {
        if (transitionBusyRef.current) return;
        const index = options.index ?? options.driver.getActiveIndex() ?? 0;
        const previousIndex = index - 1;
        if (previousIndex < 0) return;
        transitionBusyRef.current = true;
        await routeToIndex(previousIndex, options.driver, 'move');
        transitionBusyRef.current = false;
      },
      onDoneClick: async (_element: DriverHookElement, _step: DriverHookStep, options: DriverHookOptions) => {
        if (transitionBusyRef.current) return;
        transitionBusyRef.current = true;
        const index = options.index ?? options.driver.getActiveIndex() ?? TOUR_GUIDE_MOMENTS.length - 1;
        const moment = TOUR_GUIDE_MOMENTS[index];
        if (moment) {
          await persist('STEP_COMPLETED', moment.id);
          recordProductTourEventBestEffort({ event_type: 'TOUR_STEP_COMPLETED', step_id: moment.id, step_order: moment.order });
        }
        await persist('COMPLETE');
        recordProductTourEventBestEffort({ event_type: 'TOUR_COMPLETED', ...analyticsStep(moment?.id ?? null, moment?.order) });
        destroyModeRef.current = 'COMPLETE';
        options.driver.destroy();
        driverRef.current = null;
        setActive(false);
        setActiveStepId(null);
        transitionBusyRef.current = false;
        navigate('/tour/summary');
      },
      onCloseClick: async (_element: DriverHookElement, _step: DriverHookStep, options: DriverHookOptions) => {
        if (transitionBusyRef.current) return;
        transitionBusyRef.current = true;
        const index = options.index ?? options.driver.getActiveIndex() ?? 0;
        const moment = TOUR_GUIDE_MOMENTS[index];
        await persist('SKIP');
        recordProductTourEventBestEffort({ event_type: 'TOUR_SKIPPED', ...analyticsStep(moment?.id ?? null, moment?.order) });
        destroyModeRef.current = 'SKIP';
        options.driver.destroy();
        driverRef.current = null;
        setActive(false);
        setActiveStepId(null);
        transitionBusyRef.current = false;
      },
      onDestroyed: () => {
        setActive(false);
        setActiveStepId(null);
        destroyModeRef.current = null;
      },
    });
    driverRef.current = driverObj;
    return driverObj;
  }, [navigate, persist, routeToIndex]);

  const startOrResume = useCallback(async () => {
    if (driverRef.current?.isActive()) return;
    const entryPoint = getProductTourEntryPoint();
    const previousStatus = state?.status ?? 'NOT_STARTED';
    let workingState = state;
    if (!workingState) {
      workingState = await persist('START');
    } else if (workingState.status === 'SKIPPED' || workingState.status === 'COMPLETED') {
      workingState = await persist('REPLAY');
    } else {
      workingState = await persist('START') ?? workingState;
    }
    const resumeIndex = workingState?.status === 'IN_PROGRESS'
      ? Math.min(Math.max(workingState.highest_step_order, 0), TOUR_GUIDE_MOMENTS.length - 1)
      : 0;
    if (previousStatus === 'IN_PROGRESS') {
      const moment = TOUR_GUIDE_MOMENTS[resumeIndex];
      recordProductTourEventBestEffort({ event_type: 'TOUR_RESUMED', entry_point: entryPoint, ...analyticsStep(moment?.id ?? null, moment?.order) });
    } else if (previousStatus === 'SKIPPED' || previousStatus === 'COMPLETED') {
      recordProductTourEventBestEffort({ event_type: 'TOUR_REPLAYED', entry_point: entryPoint });
    } else {
      recordProductTourEventBestEffort({ event_type: 'TOUR_STARTED', entry_point: entryPoint });
    }
    const driverObj = createDriver();
    setActive(true);
    void routeToIndex(resumeIndex, driverObj, 'drive');
  }, [createDriver, persist, routeToIndex, state]);

  const skip = useCallback(async () => {
    await persist('SKIP');
    const moment = TOUR_GUIDE_MOMENTS.find((item) => item.id === activeStepId);
    recordProductTourEventBestEffort({ event_type: 'TOUR_SKIPPED', ...analyticsStep(moment?.id ?? null, moment?.order) });
    destroyModeRef.current = 'SKIP';
    driverRef.current?.destroy();
    driverRef.current = null;
    setActive(false);
    setActiveStepId(null);
  }, [activeStepId, persist]);

  const pause = useCallback(() => {
    const moment = TOUR_GUIDE_MOMENTS.find((item) => item.id === activeStepId);
    recordProductTourEventBestEffort({ event_type: 'TOUR_EXITED', ...analyticsStep(moment?.id ?? null, moment?.order) });
    destroyModeRef.current = 'PAUSE';
    driverRef.current?.destroy();
    driverRef.current = null;
    setActive(false);
    setActiveStepId(null);
  }, [activeStepId]);

  const value = useMemo<TourGuideContextValue>(() => ({ state, stateLoading, stateError, active, activeStepId, startOrResume, skip, pause, refreshState }), [active, activeStepId, pause, refreshState, skip, startOrResume, state, stateError, stateLoading]);
  return <TourGuideContext.Provider value={value}>{children}</TourGuideContext.Provider>;
}

function selectorForViewport(moment: TourGuideMoment): string {
  const mobile = window.matchMedia?.('(max-width: 575px)').matches ?? false;
  return mobile && moment.mobileSelector ? moment.mobileSelector : moment.selector;
}

export function useTourGuide() {
  const context = useContext(TourGuideContext);
  if (!context) throw new Error('useTourGuide must be used inside TourGuideProvider.');
  return context;
}
